import { REPORT_ACTIONS_BY_TARGET, type AdminReport, type ReportAction } from '@hemmafixa/shared';
import {
  Badge,
  Button,
  Card,
  DescriptionList,
  DetailLayout,
  DetailSection,
  EmptyState,
  FormField,
  StatusBadge,
  Textarea,
  cn,
  toast,
} from '@hemmafixa/ui';
import { Flag, SearchX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { BackLink } from '../shared/back-link';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useAdminReport, useReportDecision } from './api';

/** One report: what was reported, who it is about, and the decision (P5-06). */
export function ReportPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <BackLink to="/admin/reports" label={t.nav.reports} />
      <AdminReportDetail id={id} />
    </div>
  );
}

/** Loads and shows one report; the full page and the list's details panel both use it. */
export function AdminReportDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const query = useAdminReport(id);
  return (
    <QueryView query={query} loading="detail">
      {(report) =>
        report ? (
          <ReportView report={report} />
        ) : (
          <EmptyState icon={SearchX} title={t.common.notFoundTitle} description={t.common.notFoundText} />
        )
      }
    </QueryView>
  );
}

function ReportView({ report }: { report: AdminReport }) {
  const { t, dateTime } = useI18n();
  const statuses = useStatuses();
  const r = t.admin.reports;

  return (
    <>
      <Card className="grid gap-4 p-5">
        {/* Reason on the left, the key facts beside it, so the card stays short */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
            <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-danger/10 text-danger">
              <Flag className="size-6" aria-hidden />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <h1 className="truncate text-2xl font-bold text-secondary">
                {t.report.reasons[report.reason]}
              </h1>
              <p className="text-sm text-muted-foreground">{r.targets[report.target]}</p>
            </div>
          </div>
          <dl className="flex flex-wrap gap-x-8 gap-y-3">
            {[
              {
                label: r.columns.status,
                value: (
                  <Badge tone={report.status === 'open' ? 'warning' : 'neutral'}>
                    {r.statuses[report.status]}
                  </Badge>
                ),
              },
              { label: r.columns.reported, value: dateTime(report.createdAt) },
              { label: r.otherReports, value: String(report.otherReports) },
            ].map((fact) => (
              <div key={fact.label} className="grid gap-1.5">
                <dt className="text-xs text-muted-foreground">{fact.label}</dt>
                <dd className="text-sm font-medium text-secondary">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Card>
      <DetailLayout
        aside={
          <DetailSection title={t.job.details}>
            <DescriptionList
              items={[
                { label: r.reporter, value: report.reporter?.name ?? r.deleted },
                {
                  label: r.columns.subject,
                  value: report.subject ? (
                    <span className="grid gap-1">
                      <Link
                        to={`/admin/users/${report.subject.userId}`}
                        className="underline underline-offset-4"
                      >
                        {report.subject.name}
                      </Link>
                      {report.subjectStatus && (
                        <StatusBadge
                          status={report.subjectStatus}
                          statuses={statuses.user}
                          className="justify-self-start"
                        />
                      )}
                    </span>
                  ) : (
                    r.deleted
                  ),
                },
              ]}
            />
          </DetailSection>
        }
      >
        <DetailSection title={r.reported}>
          <ReportedThing report={report} />
        </DetailSection>
        {report.details && (
          <DetailSection title={r.reporterText}>
            <p className="whitespace-pre-line text-sm">{report.details}</p>
          </DetailSection>
        )}
        <DetailSection title={r.decision}>
          {report.status === 'open' ? (
            <DecisionForm report={report} />
          ) : (
            <DescriptionList
              items={[
                { label: r.columns.status, value: r.statuses[report.status] },
                ...(report.action ? [{ label: r.action, value: r.actions[report.action] }] : []),
                ...(report.resolutionNote ? [{ label: r.note, value: report.resolutionNote }] : []),
                ...(report.resolvedBy ? [{ label: r.handledBy, value: report.resolvedBy }] : []),
                ...(report.resolvedAt ? [{ label: r.handledAt, value: dateTime(report.resolvedAt) }] : []),
              ]}
            />
          )}
        </DetailSection>
      </DetailLayout>
    </>
  );
}

function ReportedThing({ report }: { report: AdminReport }) {
  const { t, dateTime } = useI18n();
  const r = t.admin.reports;
  if (report.message) {
    return (
      <div className="grid gap-3">
        <blockquote className="whitespace-pre-line rounded-lg bg-muted p-4 text-sm">
          {report.message.body}
        </blockquote>
        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {dateTime(report.message.createdAt)}
          {report.message.hidden && <Badge tone="danger">{r.hidden}</Badge>}
          {report.message.conversationStatus === 'blocked' && <Badge tone="danger">{r.blocked}</Badge>}
        </p>
      </div>
    );
  }
  if (report.proposal) {
    return (
      <div className="grid gap-3">
        <Link
          to={`/admin/review/${report.proposal.jobId}`}
          className="text-sm font-medium underline underline-offset-4"
        >
          {report.proposal.jobTitle}
        </Link>
        <blockquote className="whitespace-pre-line rounded-lg bg-muted p-4 text-sm">
          {report.proposal.message}
        </blockquote>
      </div>
    );
  }
  if (report.provider) {
    return (
      <Link to={`/admin/providers/${report.provider.id}`} className="text-sm underline underline-offset-4">
        {report.provider.companyName}
      </Link>
    );
  }
  return <p className="text-sm">{report.subject?.name}</p>;
}

/** One action and a note, or dismiss. Every choice is in the audit log. */
function DecisionForm({ report }: { report: AdminReport }) {
  const { t } = useI18n();
  const r = t.admin.reports;
  const decide = useReportDecision(report.id);
  const actions = REPORT_ACTIONS_BY_TARGET[report.target];
  const [action, setAction] = useState<ReportAction | 'dismiss'>(actions[0] ?? 'dismiss');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const dismissing = action === 'dismiss';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!dismissing && note.trim().length < 3) {
      setError(r.noteRequired);
      return;
    }
    setError(null);
    decide.mutate(
      dismissing
        ? { kind: 'dismiss', data: { note: note.trim() || undefined } }
        : { kind: 'resolve', data: { action, note: note.trim() } },
      {
        onSuccess: () => toast.success(dismissing ? r.dismissedToast : r.resolvedToast),
        onError: (failure) =>
          toast.error(failure instanceof ApiError && failure.status === 409 ? r.conflict : r.failed),
      },
    );
  };

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <fieldset className="grid gap-2">
        <legend className="mb-2 text-sm font-medium">{r.chooseAction}</legend>
        {[...actions, 'dismiss' as const].map((value) => (
          <label
            key={value}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors',
              action === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/60',
            )}
          >
            <input
              type="radio"
              name="report-action"
              value={value}
              checked={action === value}
              onChange={() => setAction(value)}
              className="mt-0.5 accent-[hsl(var(--primary))]"
            />
            <span className="grid gap-0.5">
              <span className="font-medium">{value === 'dismiss' ? r.dismiss : r.actions[value]}</span>
              <span className="text-muted-foreground">
                {value === 'dismiss' ? r.dismissText : r.actionTexts[value]}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
      <FormField label={dismissing ? r.noteOptional : r.note} hint={r.noteHint} error={error ?? undefined}>
        <Textarea rows={3} maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} />
      </FormField>
      <Button
        type="submit"
        variant={action === 'account_suspended' ? 'danger' : 'primary'}
        loading={decide.isPending}
        className="justify-self-start"
      >
        {dismissing ? r.dismiss : fill(r.confirm, { action: r.actions[action] })}
      </Button>
    </form>
  );
}
